import React from 'react';
import { Alert, Collapse, Tag, Typography } from 'antd';
import ReactMarkdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkMath from 'remark-math';
import 'katex/dist/katex.min.css';

import { InfoPoint } from '../BondsPlayground/ModelGuide';

const GENERATION_MATH = String.raw`
### Fixed coupon at legal issuance

For legal issue date $d$, the latest complete, non-crossed source observation
at or before the end of that day is the fixing. Its ask yield becomes the fixed
annual coupon $c$. The source observation may predate $d$; the legal issue,
maturity, and semiannual coupon calendar remain anchored to $d$.

### Moving interpolated yield

For residual maturity $T_L \le R(t) \le T_U$ and market side
$s\in\{bid,ask\}$,

$$
w(t)=\frac{R(t)-T_L}{T_U-T_L},\qquad
y_s(t)=(1-w(t))y_{L,s}(t)+w(t)y_{U,s}(t).
$$

### Dirty and clean price

With face $F=100$, two coupons per year, coupon cash $C=Fc/2$, $N(t)$
remaining payments, and fractional time $u(t)$ to the next payment,

$$
P_s^{dirty}(t)=
\sum_{j=0}^{N(t)-2}\frac{C}{(1+y_s(t)/2)^{u(t)+j}}
+\frac{F+C}{(1+y_s(t)/2)^{u(t)+N(t)-1}}.
$$

Accrued interest is removed to publish PM's clean MarketHistory quotes:

$$
AI(t)=C\frac{\text{elapsed since previous coupon}}
{\text{coupon-period length}},\qquad
P_s^{clean}(t)=P_s^{dirty}(t)-AI(t).
$$
`;

const STEPS = [
  [
    'Define one family',
    'Choose one currency, one reviewed source asset for each maintained tenor, and metadata applied uniformly to every generated asset.',
  ],
  [
    'Preview immutably',
    'The server resolves legal quarterly dates, carried-forward issue fixings, identifiers, expected rows, warnings, and conflicts without creating a bond or MarketHistory row.',
  ],
  [
    'Publish exactly that review',
    'Create accepts only the reviewed batch ID and checksum. It creates AFSBond assets and clean bid/ask MarketHistory without overwriting published output.',
  ],
  [
    'Trace every price',
    'The review and result preserve the actual source rows and timestamps used for coupon fixing and each interpolated valuation.',
  ],
];

const SyntheticBondGenerationGuide: React.FC = () => (
  <section className="synthetic-bonds__guide">
    <div className="synthetic-bonds__guide-heading">
      <div>
        <Tag color="blue">Controlled historical generation</Tag>
        <Tag>AFS Bond · V2 pricing</Tag>
        <Typography.Title level={4}>
          Yield-derived synthetic bonds
        </Typography.Title>
      </div>
      <InfoPoint title="Preview versus Create">
        Preview freezes auditable review records only. Create is the separate
        step that persists AFSBond assets and clean MarketHistory rows.
      </InfoPoint>
    </div>

    <Typography.Paragraph type="secondary">
      Issue quarterly vintages whose fixed coupons are observed at issuance and
      whose clean bid/ask histories age continuously along seven reviewed yield
      nodes. This avoids the price discontinuities of a constant-maturity feed.
    </Typography.Paragraph>

    <div className="synthetic-bonds__guide-steps">
      {STEPS.map(([title, body], index) => (
        <div className="synthetic-bonds__guide-step" key={title}>
          <span>{index + 1}</span>
          <div>
            <Typography.Text strong>{title}</Typography.Text>
            <Typography.Paragraph>{body}</Typography.Paragraph>
          </div>
        </div>
      ))}
    </div>

    <Alert
      description="V2 interpolates yield-to-maturity rather than bootstrapped spot rates, carries valid system observations forward without a staleness rejection, and stops pricing below 6M residual maturity."
      message="Concept-model boundary"
      showIcon
      type="info"
    />

    <Collapse
      className="synthetic-bonds__formula-drawer"
      items={[
        {
          key: 'formulas',
          label: (
            <span className="synthetic-bonds__formula-label">
              Calculation formulas and definitions
              <Tag color="geekblue">KaTeX</Tag>
            </span>
          ),
          children: (
            <div className="bonds-playground__math">
              <ReactMarkdown
                rehypePlugins={[rehypeKatex]}
                remarkPlugins={[remarkMath]}
              >
                {GENERATION_MATH}
              </ReactMarkdown>
            </div>
          ),
        },
      ]}
    />
  </section>
);

export default SyntheticBondGenerationGuide;
